/**
 * 2026-10-05: "Data delayed" banner. From 2026-10-03 to 2026-10-05 every
 * Navusoft export for Delta was refused and Delta found the stale reports
 * themselves. A report whose data is older than its schedule allows now says
 * so at the top, before the client has to notice:
 *
 *   Data delayed: last updated <time>. Meridian has been alerted and is
 *   working on it.
 *
 * Same clock as the pipeline's freshness watcher (alerts/freshness.py), which
 * escalates to Meridian at the same moment:
 *
 *   - a cadence entity (a lane's sync entity: SERVICE_EVENT, AR_CURRENT) is
 *     late `cadence_grace_hours` past its last expected pull (a lane fire or
 *     the daily export);
 *   - a daily entity (fed only by the daily export: INVOICE, DISPOSAL, ...)
 *     is late `daily_grace_hours` past the daily export, i.e. it missed one.
 *
 * Both graces come from customers/<slug>/config.yaml `data_freshness`
 * (default 2 hours). The schedule is the tenant's own `navusoft_portal`
 * lanes and daily export, each in its own time zone; resolve-manifest.mjs
 * stamps it on the manifest as `data_schedule` (dataScheduleFromConfig).
 *
 * "Last updated" is the stamp the "Updated @" chip reads (tabFreshness.mjs
 * stampFromRows, combined with the newest loaded source file by
 * combineSourceStamp). No cause is ever shown to the client.
 *
 * Plain .mjs so node:test drives it (scripts/data-delay.test.mjs).
 */

export const DEFAULT_CADENCE_GRACE_HOURS = 2;
export const DEFAULT_DAILY_GRACE_HOURS = 2;

export const CADENCE = "cadence";
export const DAILY = "daily";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const SLOT_SEARCH_DAYS = 8;

/**
 * Navusoft report key -> the upload source it produces. Mirrors
 * navusoft_export/plan.py DEFAULT_REPORTS (pinned by the test). Reports in
 * REPORT_DEFAULT_OFF run only when config `enable_reports` lists them.
 */
export const REPORT_SOURCE = Object.freeze({
  work_orders: "navusoft_wos",
  revenue: "navusoft_revenue",
  disposal: "navusoft_disposal",
  ar_history: "ar_transactions",
  aging: "ar_current",
  contracts: "site_contracts",
  ops_control_total: "ops_control_total",
  vehicles: "navusoft_vehicles",
  active_services: "active_services",
  route_productivity: "navusoft_route_productivity",
});
export const REPORT_DEFAULT_OFF = Object.freeze([
  "vehicles",
  "active_services",
  "route_productivity",
]);

/**
 * Upload source -> entity. Mirrors connectors/upload.py SOURCE_TO_ENTITY for
 * every source a scheduled entity can come from (pinned by the test).
 */
export const SOURCE_ENTITY = Object.freeze({
  navusoft_wos: "SERVICE_EVENT",
  canonical_service_event: "SERVICE_EVENT",
  navusoft_revenue: "INVOICE",
  canonical_invoice: "INVOICE",
  navusoft_disposal: "DISPOSAL",
  navusoft_vehicles: "VEHICLE",
  ar_current: "AR_CURRENT",
  ar_aging: "AR_CURRENT",
  canonical_ar_current: "AR_CURRENT",
  ar_transactions: "AR_TRANSACTIONS",
  canonical_ar_transactions: "AR_TRANSACTIONS",
  site_contracts: "SITE_CONTRACTS",
  canonical_site_contracts: "SITE_CONTRACTS",
  ops_control_total: "OPS_CONTROL_TOTAL",
  active_services: "ACTIVE_SERVICES",
  navusoft_route_productivity: "ROUTE_DAY",
});

/** The upload sources that feed `entity` (sorted), [] when none. */
export function sourcesForEntity(entity) {
  return Object.keys(SOURCE_ENTITY)
    .filter((s) => SOURCE_ENTITY[s] === entity)
    .sort();
}

/** A cron hour field ("5-20", "5,11,17,23", "*\/6", "8-18/2", "*", 7) -> sorted hours. */
export function expandHours(spec) {
  const text = typeof spec === "boolean" ? "" : String(spec ?? "").trim();
  if (!text) throw new Error(`hours ${JSON.stringify(spec)} is empty`);
  const out = new Set();
  for (const raw of text.split(",")) {
    const item = raw.trim();
    const m = /^(\*|(\d{1,2})(?:-(\d{1,2}))?)(?:\/(\d{1,2}))?$/.exec(item);
    if (!m) throw new Error(`hours item ${JSON.stringify(item)} is not N, A-B, * or a step`);
    const step = m[4] ? Number(m[4]) : 1;
    let lo;
    let hi;
    if (m[1] === "*") {
      lo = 0;
      hi = 23;
    } else {
      lo = Number(m[2]);
      hi = m[3] ? Number(m[3]) : m[4] ? 23 : lo;
    }
    if (!(lo >= 0 && lo <= hi && hi <= 23) || step < 1) {
      throw new Error(`hours item ${JSON.stringify(item)} is outside 0 to 23`);
    }
    for (let h = lo; h <= hi; h += step) out.add(h);
  }
  return [...out].sort((a, b) => a - b);
}

/** A cron day_of_week field ("*", "mon-sat", "mon,wed") -> day names in week order. */
export function expandDays(spec) {
  const text = String(spec ?? "")
    .trim()
    .toLowerCase();
  if (text === "" || text === "*") return [...DAYS];
  const out = new Set();
  for (const raw of text.split(",")) {
    const ends = raw.trim().split("-");
    if (ends.length > 2 || ends.some((e) => !DAYS.includes(e))) {
      throw new Error(`days item ${JSON.stringify(raw)} is not a day or a range`);
    }
    const lo = DAYS.indexOf(ends[0]);
    const hi = DAYS.indexOf(ends[ends.length - 1]);
    if (lo > hi) throw new Error(`days range ${JSON.stringify(raw)} runs backwards`);
    for (let i = lo; i <= hi; i += 1) out.add(DAYS[i]);
  }
  return DAYS.filter((d) => out.has(d));
}

function grace(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function sameSlot(a, b) {
  return (
    a.minute === b.minute &&
    a.timezone === b.timezone &&
    a.hours.join(",") === b.hours.join(",") &&
    a.days.join(",") === b.days.join(",")
  );
}

/**
 * customers/<slug>/config.yaml -> the manifest's `data_schedule`, or null for
 * a tenant with no Navusoft schedule (no banner can ever show). Mirrors
 * alerts/freshness.py expectations_for.
 *
 *   { cadence_grace_hours, daily_grace_hours,
 *     entities: { SERVICE_EVENT: { kind: "cadence",
 *                                  slots: [{ hours, days, minute, timezone }] } } }
 */
export function dataScheduleFromConfig(config) {
  const portal = config?.navusoft_portal;
  if (!portal || typeof portal !== "object") return null;
  const entities = {};
  const add = (entity, kind, slot) => {
    const e = (entities[entity] ??= { kind, slots: [] });
    if (kind === CADENCE) e.kind = CADENCE;
    if (!e.slots.some((s) => sameSlot(s, slot))) e.slots.push(slot);
  };
  const lanes = portal.lanes && typeof portal.lanes === "object" ? portal.lanes : {};
  for (const lane of Object.values(lanes)) {
    if (!lane || lane.enabled !== true) continue;
    const slot = {
      hours: expandHours(lane.hours),
      days: expandDays(lane.days ?? "*"),
      minute: Number.isInteger(lane.minute) ? lane.minute : 35,
      timezone: String(lane.timezone ?? "America/Chicago").trim(),
    };
    for (const entity of lane.sync_entities ?? []) {
      add(String(entity).trim().toUpperCase(), CADENCE, slot);
    }
  }
  const sched = portal.schedule;
  if (sched && sched.enabled === true && sched.load !== false) {
    const slot = {
      hours: [Number.isInteger(sched.hour) ? sched.hour : 22],
      days: expandDays(sched.day_of_week ?? "sun"),
      minute: Number.isInteger(sched.minute) ? sched.minute : 0,
      timezone: String(sched.timezone ?? "America/Chicago").trim(),
    };
    const enable = new Set(portal.enable_reports ?? []);
    const disable = new Set(portal.disable_reports ?? []);
    for (const [key, source] of Object.entries(REPORT_SOURCE)) {
      const on = (!REPORT_DEFAULT_OFF.includes(key) || enable.has(key)) && !disable.has(key);
      if (!on) continue;
      const entity = SOURCE_ENTITY[source];
      // A cadence entity keeps its kind; the daily export is one more pull.
      add(entity, entities[entity]?.kind ?? DAILY, slot);
    }
  }
  if (Object.keys(entities).length === 0) return null;
  const block = config?.data_freshness ?? {};
  return {
    cadence_grace_hours: grace(block.cadence_grace_hours, DEFAULT_CADENCE_GRACE_HOURS),
    daily_grace_hours: grace(block.daily_grace_hours, DEFAULT_DAILY_GRACE_HOURS),
    entities,
  };
}

// ── time zone arithmetic (no library: Intl only) ────────────────────────

const partsCache = new Map();
function formatter(timeZone) {
  let f = partsCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "short",
    });
    partsCache.set(timeZone, f);
  }
  return f;
}

function zoned(ms, timeZone) {
  const out = {};
  for (const p of formatter(timeZone).formatToParts(new Date(ms))) out[p.type] = p.value;
  return {
    y: Number(out.year),
    mo: Number(out.month),
    d: Number(out.day),
    h: Number(out.hour),
    mi: Number(out.minute),
    s: Number(out.second),
    wd: out.weekday.toLowerCase().slice(0, 3),
  };
}

function offsetMs(ms, timeZone) {
  const z = zoned(ms, timeZone);
  return Date.UTC(z.y, z.mo - 1, z.d, z.h, z.mi, z.s) - Math.floor(ms / 1000) * 1000;
}

/** The instant of a wall-clock time in `timeZone` (DST-correct). */
export function zonedInstant(y, mo, d, h, mi, timeZone) {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const first = guess - offsetMs(guess, timeZone);
  return guess - offsetMs(first, timeZone);
}

function* candidates(slot, aroundMs, backwards) {
  const step = backwards ? -1 : 1;
  const hours = [...slot.hours].sort((a, b) => (backwards ? b - a : a - b));
  const start = zoned(aroundMs, slot.timezone);
  for (let n = 0; n <= SLOT_SEARCH_DAYS; n += 1) {
    // The calendar day n days away, read in the slot's zone at noon.
    const noon = zonedInstant(start.y, start.mo, start.d, 12, 0, slot.timezone) + step * n * 864e5;
    const day = zoned(noon, slot.timezone);
    if (!slot.days.includes(day.wd)) continue;
    for (const h of hours) yield zonedInstant(day.y, day.mo, day.d, h, slot.minute, slot.timezone);
  }
}

/** The latest scheduled pull at or before `at` (Date), or null. */
export function lastExpected(slots, at) {
  const ms = at.getTime();
  let best = null;
  for (const slot of slots ?? []) {
    for (const c of candidates(slot, ms, true)) {
      if (c <= ms) {
        if (best === null || c > best) best = c;
        break;
      }
    }
  }
  return best === null ? null : new Date(best);
}

/** The earliest scheduled pull strictly after `at`, or null. */
export function nextExpected(slots, at) {
  const ms = at.getTime();
  let best = null;
  for (const slot of slots ?? []) {
    for (const c of candidates(slot, ms, false)) {
      if (c > ms) {
        if (best === null || c < best) best = c;
        break;
      }
    }
  }
  return best === null ? null : new Date(best);
}

/**
 * One entity's verdict. `stampAt` is the chip's Date (or null: no record,
 * which never shows a banner). Returns null when the schedule says nothing
 * about the entity, else { entity, delayed, lastUpdated, due }.
 */
export function entityDelay(schedule, entity, stampAt, now) {
  const exp = schedule?.entities?.[entity];
  if (!exp) return null;
  const hours = exp.kind === CADENCE ? schedule.cadence_grace_hours : schedule.daily_grace_hours;
  const g = grace(hours, DEFAULT_CADENCE_GRACE_HOURS) * 3600e3;
  const due = lastExpected(exp.slots, new Date(now.getTime() - g));
  if (!(stampAt instanceof Date) || Number.isNaN(stampAt.getTime())) {
    return { entity, delayed: false, lastUpdated: null, due };
  }
  return { entity, delayed: due !== null && stampAt < due, lastUpdated: stampAt, due };
}

/**
 * The banner for a page reading `entities` (one for a report, every
 * scheduled entity for the Executive): null when nothing is delayed, else
 * { lastUpdated } = the oldest delayed stamp.
 */
export function pageDelay(schedule, stamps, now) {
  let oldest = null;
  for (const [entity, at] of Object.entries(stamps ?? {})) {
    const v = entityDelay(schedule, entity, at, now);
    if (v?.delayed && (oldest === null || v.lastUpdated < oldest)) oldest = v.lastUpdated;
  }
  return oldest === null ? null : { lastUpdated: oldest };
}

/** The banner's words. `label` is formatUpdatedLabel's ("Oct 3, 7:52 AM"). */
export function delayBannerText(label) {
  return `Data delayed: last updated ${label}. Meridian has been alerted and is working on it.`;
}

/** The entities a page shows the banner for: the given ones that are scheduled. */
export function scheduledEntities(schedule, entities) {
  const all = Object.keys(schedule?.entities ?? {});
  if (!entities) return all.sort();
  return entities.filter((e) => typeof e === "string" && all.includes(e));
}
